import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { CourseIDContext } from '../context/CourseIDContext';
import { fetchStudentAnswersHistory, fetchUserAnswersAsOfDate, getESTDateString, getSignificantRevisions } from '../utils/serverFetchAndAddUtilities';
import QAndA from '../organisms/QAndA';
import { AuthContext } from '../context/AuthContext';
import './IndividualQuestionStudentHistory.css';

const splitEssayContent = (essayAnswer) => {
  if (!essayAnswer) {
    return { content: '', references: '' };
  }
  const parts = essayAnswer.split('|||||');
  return {
    content: parts[0] || '',
    references: parts[1] || '',
  };
};

const formatTimestamp = (value) => {
  if (!value || typeof value !== 'string') {
    return 'Date unavailable';
  }

  const sanitized = value.includes('T') ? value.split('T')[0] : value;
  const parts = sanitized.split('-');

  if (parts.length !== 3) {
    return sanitized;
  }

  const [year, monthStr, dayStr] = parts;
  const monthIndex = parseInt(monthStr, 10) - 1;
  const day = parseInt(dayStr, 10);

  if (
    Number.isNaN(monthIndex) ||
    Number.isNaN(day) ||
    monthIndex < 0 ||
    monthIndex > 11 ||
    day < 1 ||
    day > 31
  ) {
    return sanitized;
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return `${monthNames[monthIndex]} ${day}, ${year}`;
};

// Sentinel strings injected as tokens to preserve paragraph/line structure.
const PARA_BREAK = '\x00PARA\x00';
const LINE_BREAK = '\x00LINE\x00';

// Tokenize text into words plus structural break sentinels.
const tokenizeForDiff = (text) =>
  (text || '')
    .replace(/\n\n+/g, ` ${PARA_BREAK} `)
    .replace(/\n/g, ` ${LINE_BREAK} `)
    .split(/\s+/)
    .filter(Boolean);

// LCS-based word diff. Returns [{type:'equal'|'added'|'removed', word:string}].
// Paragraph/line break sentinels are treated as ordinary tokens so structural
// changes survive the diff and render correctly.
const computeWordDiff = (oldText, newText) => {
  const oldWords = tokenizeForDiff(oldText);
  const newWords = tokenizeForDiff(newText);
  const m = oldWords.length;
  const n = newWords.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = oldWords[i-1] === newWords[j-1]
        ? dp[i-1][j-1] + 1
        : Math.max(dp[i-1][j], dp[i][j-1]);
  const tokens = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i-1] === newWords[j-1]) {
      tokens.unshift({ type: 'equal', word: oldWords[i-1] }); i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) {
      tokens.unshift({ type: 'added', word: newWords[j-1] }); j--;
    } else {
      tokens.unshift({ type: 'removed', word: oldWords[i-1] }); i--;
    }
  }
  return tokens;
};

// Format a Unix ms timestamp as a date in Eastern time, e.g. "January 15, 2025"
const formatDateFromUnixMs = (unixMs) => {
  if (!unixMs) return 'Date unavailable';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(Number(unixMs));
};

// Format a Unix ms timestamp as a time in Eastern time, e.g. "2:30 PM"
const formatTimeFromUnixMs = (unixMs) => {
  if (!unixMs) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(Number(unixMs));
};


const IndividualQuestionStudentHistory = ({ username, questionID, showSnapshotPicker = true }) => {
  const { courseDesignState } = useContext(CourseDesignContext);
  const { courseID } = useContext(CourseIDContext);
  const { getFullNameByUsername } = useContext(AuthContext);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [snapshotAnswers, setSnapshotAnswers] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState(null);
  const [snapshotInputValue, setSnapshotInputValue] = useState('');
  const [fullName, setFullName] = useState(username);

  const question = useMemo(
    () =>
      courseDesignState.find(
        (item) => item.dataID === questionID && item.dataType === 'question'
      ) || null,
    [courseDesignState, questionID]
  );

  const questionOptions = useMemo(() => {
    if (!question?.questionOptions) {
      return [];
    }
    return question.questionOptions.split('|||').map((option) => option.trim());
  }, [question]);

  useEffect(() => {
    let ignore = false;
    const loadFullName = async () => {
      try {
        const name = await getFullNameByUsername(username);
        if (!ignore && name) {
          setFullName(name);
        }
      } catch {
        // fallback to username already in state
      }
    };
    loadFullName();
    return () => {
      ignore = true;
    };
  }, [username, getFullNameByUsername]);

  useEffect(() => {
    let isMounted = true;

    const loadHistory = async () => {
      if (!courseID || !username || !questionID) {
        setHistory([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const allHistory = await fetchStudentAnswersHistory(courseID, username);
        if (!isMounted) return;
        const sorted = (allHistory || [])
          .filter((entry) => entry.questionID === questionID)
          .sort((a, b) => {
            const aTime = a.savedAt != null ? Number(a.savedAt) : new Date(a.submissionDate || 0).getTime();
            const bTime = b.savedAt != null ? Number(b.savedAt) : new Date(b.submissionDate || 0).getTime();
            return aTime - bTime;
          });
        const filtered = getSignificantRevisions(sorted);
        const normalizedHistory = filtered.map((entry) => {
          // lastSavedAtMs drives both date label and time label for new-schema rows.
          // Falls back to submissionDate/updatedAt for any pre-migration rows.
          const lastSavedAtMs = entry.lastSavedAt != null ? Number(entry.lastSavedAt)
                              : entry.savedAt    != null ? Number(entry.savedAt)
                              : null;
          const rawDate = lastSavedAtMs
            ? getESTDateString(lastSavedAtMs)
            : ((entry.submissionDate || entry.updatedAt || '').split('T')[0]) || null;
          return {
            ...entry,
            lastSavedAtMs,
            rawDate,
          };
        });
        setHistory(normalizedHistory);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch question history:', err);
        if (!isMounted) return;
        setError('Unable to load answer history right now.');
        setHistory([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      isMounted = false;
    };
  }, [courseID, username, questionID]);

  const handleSnapshotView = useCallback(async (rawDate) => {
    if (!rawDate || !courseID || !username) {
      return;
    }

    setSelectedDate(rawDate);
    setSnapshotInputValue(rawDate);
    setSnapshotLoading(true);
    setSnapshotError(null);
    setSnapshotAnswers(null);

    try {
      const answers = await fetchUserAnswersAsOfDate(courseID, username, rawDate);
      const answerList = (answers && answers[username]) || [];
      setSnapshotAnswers({ [username]: answerList });
    } catch (err) {
      console.error('Unable to fetch answers as of date:', err);
      setSnapshotError('Unable to load responses for this date.');
    } finally {
      setSnapshotLoading(false);
    }
  }, [courseID, username]);

  const formattedHistory = useMemo(() => {
    // history is in chronological order (oldest first); we map it that way so
    // each entry can reference its predecessor, then reverse for display.
    const entries = history.map((entry, index) => {
      const submissionLabel = entry.lastSavedAtMs
        ? formatDateFromUnixMs(entry.lastSavedAtMs)
        : formatTimestamp(entry.rawDate);
      const timeLabel = entry.lastSavedAtMs
        ? formatTimeFromUnixMs(entry.lastSavedAtMs)
        : '';
      const baseKey = `${entry.rawDate || 'unknown'}-${index}`;

      if (question?.questionType === 'LongAnswer') {
        const { content, references } = splitEssayContent(entry.questionEssayAnswer);

        // For all revisions after the first, check whether the change is small
        // enough to show as a diff instead of the full text.
        let diffTokens = null;
        if (index > 0) {
          const prevContent = splitEssayContent(history[index - 1].questionEssayAnswer).content;
          diffTokens = computeWordDiff(prevContent, content);
        }

        return {
          key: baseKey,
          submissionLabel,
          rawDate: entry.rawDate,
          timeLabel,
          content,
          references,
          rawAnswer: entry.questionEssayAnswer,
          diffTokens,
          type: 'essay',
        };
      }

      if (question?.questionType === 'Checkbox') {
        const selections = new Set(
          (entry.questionAnswer || '')
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value.length > 0)
        );
        const options = questionOptions.length
          ? questionOptions.map((option, optionIndex) => ({
              key: `${baseKey}-option-${optionIndex}`,
              label: option,
              selected: selections.has((optionIndex + 1).toString()),
            }))
          : [];
        return {
          key: baseKey,
          submissionLabel,
          rawDate: entry.rawDate,
          timeLabel,
          options,
          type: 'checkbox',
        };
      }

      if (question?.questionType === 'Radio') {
        const selectedValue = (entry.questionAnswer || '').trim();
        const options = questionOptions.length
          ? questionOptions.map((option, optionIndex) => ({
              key: `${baseKey}-option-${optionIndex}`,
              label: option,
              selected: selectedValue === (optionIndex + 1).toString(),
            }))
          : [];
        return {
          key: baseKey,
          submissionLabel,
          rawDate: entry.rawDate,
          timeLabel,
          options,
          type: 'radio',
        };
      }

      const fallbackContent =
        entry.questionAnswer ||
        entry.questionEssayAnswer ||
        'No answer saved.';

      return {
        key: baseKey,
        submissionLabel,
        rawDate: entry.rawDate,
        timeLabel,
        content: fallbackContent,
        type: 'generic',
      };
    });

    return entries.reverse(); // most recent first
  }, [history, question, questionOptions]);

  if (!username || !questionID) {
    return (
      <div className="question-history-page">
        <div className="question-history-container">
          <p>Missing username or question information.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="question-history-page">
      <div className="question-history-container">

        {loading && <div>Loading answer history…</div>}
        {error && <div className="question-history-error">{error}</div>}

        {!loading && !error && formattedHistory.length === 0 && (
          <div>No saved answers found for this question.</div>
        )}

        {!loading && !error && formattedHistory.length > 0 && (
          <div className="question-history-list">
            {formattedHistory.map((entry) => (
              <div className="question-history-item" key={entry.key}>
                <button
                  type="button"
                  className="question-history-date"
                  onClick={() => handleSnapshotView(entry.rawDate)}
                >
                  {entry.timeLabel
                    ? `${entry.submissionLabel}\n${entry.timeLabel}`
                    : entry.submissionLabel}
                </button>
                <div className="question-history-answer">
                  <div className="question-history-answer-content">
                    {entry.type === 'essay' && (
                      <>
                        <div className="longAnswerTextForPDF">
                          {entry.diffTokens ? (
                            entry.diffTokens.map((token, i) => {
                              if (token.word === PARA_BREAK) {
                                if (token.type === 'equal' || token.type === 'added') return <React.Fragment key={i}><br /><br /></React.Fragment>;
                                return <span key={i} className="historyDiffRemoved">¶ </span>;
                              }
                              if (token.word === LINE_BREAK) {
                                if (token.type === 'equal') return <br key={i} />;
                                return <span key={i} className={token.type === 'added' ? 'historyDiffAdded' : 'historyDiffRemoved'}>↵ </span>;
                              }
                              return (
                                <span
                                  key={i}
                                  className={
                                    token.type === 'added'   ? 'historyDiffAdded'
                                  : token.type === 'removed' ? 'historyDiffRemoved'
                                  : undefined
                                  }
                                >
                                  {token.word}{' '}
                                </span>
                              );
                            })
                          ) : (
                            entry.content
                          )}
                        </div>
                        {entry.references && (
                          <div className="question-history-referencesBox">
                            <div className="question-history-referencesText">
                              {entry.references}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {(entry.type === 'radio' || entry.type === 'checkbox') && entry.options?.length > 0 && (
                      <div className="optionsContainer question-history-options">
                        {entry.options.map((option) => (
                          <div
                            key={option.key}
                            className={`${entry.type === 'radio' ? 'radioButton' : 'checkboxButton'}${
                              option.selected ? ' selected' : ''
                            }`}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                    {entry.type === 'generic' && (
                      <div className="question-history-textAnswer">{entry.content}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showSnapshotPicker && <div className="question-history-snapshot">
          <div className="question-history-datePicker">
            <input
              id="snapshot-date-input"
              name="snapshot-date-input"
              type="date"
              value={snapshotInputValue}
              onChange={(event) => {
                const newValue = event.target.value;
                setSnapshotInputValue(newValue);
                if (newValue) {
                  handleSnapshotView(newValue);
                } else {
                  setSelectedDate(null);
                  setSnapshotAnswers(null);
                  setSnapshotError(null);
                }
              }}
            />
          </div>
          {selectedDate && (
            <>
              {snapshotLoading && <div>Loading answers…</div>}
              {snapshotError && <div className="question-history-error">{snapshotError}</div>}
              {!snapshotLoading && !snapshotError && snapshotAnswers && (
                <QAndA
                  username={username}
                  studentAnswers={snapshotAnswers}
                  answersReadOnly={true}
                  sectionsToShow="all"
                  showSectionHeaders={true}
                  ignoreCurrentCourseDate={true}
                  showMyPhiHeader={false}
                  showQuestionHistoryButton={true}
                />
              )}
            </>
          )}
        </div>}
      </div>
    </div>
  );
};

export default IndividualQuestionStudentHistory;
